// useChartUpload.ts
import { useState } from "react";
import { App } from "antd";
import { useUploadFile } from "../../../../../../hooks/file";
import {
  useMessageError,
  useMessageSuccess,
} from "../../../../../../hooks/common";

export interface ChartUploadResult {
  id: string;
  name: string;
  type: string;
  fileLink: string;
}

// Keep in sync with the backend upload limit (100 MB).
const MAX_FILE_SIZE = 100 * 1000 * 1000;

export const useChartUpload = () => {
  const { message } = App.useApp();
  const uploadFileHook = useUploadFile("common/r2/upload-image", true, true);
  const [uploading, setUploading] = useState(false);

  // Surface backend errors (e.g. 422 "current file size is …") and successes
  // via the shared, context-aware message hooks.
  useMessageError([uploadFileHook]);
  useMessageSuccess([uploadFileHook], "Chart uploaded successfully");

  const uploadMultiple = async (
    files: File[],
  ): Promise<ChartUploadResult[]> => {
    setUploading(true);
    const results: ChartUploadResult[] = [];

    try {
      for (const file of files) {
        // Guard client-side so oversized files fail fast with a clear message
        // instead of relying on the raw backend validation string.
        // if (file.size > MAX_FILE_SIZE) {
        //   message.error(
        //     `${file.name} is too large. Maximum allowed size is 100 MB.`,
        //   );
        //   continue;
        // }

        const formData = new FormData();
        formData.append("file", file);

        const response = await uploadFileHook.fetch(formData);

        // On failure the hook resolves with the Axios error object (no `url`);
        // useMessageError already surfaces the reason, so just skip it here.
        if (response?.url) {
          results.push({
            id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            name: file.name,
            type: file.name.split(".").pop() || "",
            fileLink: response.url,
          });
        }
      }
    } finally {
      setUploading(false);
    }

    return results;
  };
  return { uploadMultiple, uploading };
};
